import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw, RouteLocationNormalized } from 'vue-router'
import { useAuthStore } from '@/stores/useAuthStore'
import type { UserRole } from '@/types'

// ============================================
// Route Meta Types
// ============================================

declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean
    roles?: UserRole[]
    title?: string
  }
}

// ============================================
// Routes Configuration
// ============================================

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'home',
    redirect: '/dashboard'
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: {
      requiresAuth: false,
      title: 'Connexion'
    }
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('@/views/DashboardView.vue'),
    meta: {
      requiresAuth: true,
      roles: ['admin', 'standard', 'guest'],
      title: 'Tableau de bord'
    }
  },
  {
    path: '/quote/:id',
    name: 'quote-edit',
    component: () => import('@/views/QuoteEditView.vue'),
    meta: {
      requiresAuth: true,
      roles: ['admin', 'standard', 'guest'],
      title: 'Devis'
    }
  },
  {
    path: '/catalogue',
    name: 'catalogue',
    component: () => import('@/views/CatalogueView.vue'),
    meta: {
      requiresAuth: true,
      roles: ['admin', 'standard', 'guest'],
      title: 'Catalogue'
    }
  },
  // Admin routes
  {
    path: '/admin',
    name: 'admin',
    component: () => import('@/views/AdminView.vue'),
    meta: {
      requiresAuth: true,
      roles: ['admin'],
      title: 'Administration'
    }
  },
  {
    path: '/admin/queue',
    name: 'admin-queue',
    component: () => import('@/views/AdminQueueView.vue'),
    meta: {
      requiresAuth: true,
      roles: ['admin'],
      title: 'Devis en attente'
    }
  },
  {
    path: '/admin/notes',
    name: 'admin-notes',
    component: () => import('@/views/AdminNotesView.vue'),
    meta: {
      requiresAuth: true,
      roles: ['admin'],
      title: 'Notes à chiffrer'
    }
  },
  // Catch-all route
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    redirect: '/dashboard'
  }
]

// ============================================
// Router Instance
// ============================================

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes
})

// ============================================
// Navigation Guards
// ============================================

/**
 * Check if user can access route based on authentication and roles
 */
function canAccessRoute(to: RouteLocationNormalized): boolean {
  const authStore = useAuthStore()

  // Route doesn't require auth
  if (to.meta.requiresAuth === false) {
    return true
  }

  // Check authentication
  if (!authStore.isAuthenticated) {
    return false
  }

  // Check session validity
  if (!authStore.checkSession()) {
    return false
  }

  // Check roles if specified
  if (to.meta.roles && to.meta.roles.length > 0) {
    const userRole = authStore.userRole
    if (!userRole || !to.meta.roles.includes(userRole)) {
      return false
    }
  }

  return true
}

/**
 * Global navigation guard
 */
router.beforeEach((to, _from, next) => {
  const authStore = useAuthStore()

  // Update document title
  document.title = to.meta.title
    ? `${to.meta.title} - VFR Devis`
    : 'VFR Devis Digital'

  // If going to login and already authenticated, redirect to dashboard
  if (to.name === 'login' && authStore.isAuthenticated) {
    console.log('[Router] Already authenticated, redirecting to dashboard')
    return next({ name: 'dashboard' })
  }

  // Check access
  if (!canAccessRoute(to)) {
    console.log('[Router] Access denied, redirecting to login')

    // Store intended destination for redirect after login
    if (to.name !== 'login') {
      sessionStorage.setItem('redirectAfterLogin', to.fullPath)
    }

    return next({ name: 'login' })
  }

  next()
})

/**
 * After navigation hook
 */
router.afterEach((to) => {
  // Check for redirect after login
  if (to.name === 'dashboard') {
    const redirectPath = sessionStorage.getItem('redirectAfterLogin')
    if (redirectPath && redirectPath !== '/dashboard') {
      sessionStorage.removeItem('redirectAfterLogin')
      router.push(redirectPath)
    }
  }
})

export default router
